import * as React from "react";
import { cn } from "../lib/cn";

export interface AdherenceRingProps {
  /** 0–100 */
  percent: number;
  size?: number;
  label?: string;
  className?: string;
}

/**
 * The clinic/patient adherence donut from the imported design — a single deterministic
 * number (mean 30-day adherence, or clinic-wide mean), never an AI-generated score. Animates
 * in from empty on mount to draw attention without implying anything is "live."
 */
export function AdherenceRing({ percent, size = 140, label, className }: AdherenceRingProps) {
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setTimeout(() => setDrawn(true), 60));
    return () => cancelAnimationFrame(id);
  }, []);

  const stroke = Math.round(size * 0.078);
  const radius = size / 2 - stroke;
  const circumference = 2 * Math.PI * radius;
  const offset = drawn ? circumference * (1 - percent / 100) : circumference;
  const center = size / 2;

  return (
    <div className={cn("relative", className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="currentColor" className="text-charcoal-900/10" strokeWidth={stroke} />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-primary-600"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(.3,.9,.2,1)" }}
        />
      </svg>
      <div dir="ltr" className="absolute inset-0 flex items-center justify-center">
        <span className="font-semibold text-charcoal-900" style={{ fontSize: size * 0.22 }}>
          {percent}%
        </span>
      </div>
      {label && <span className="sr-only">{label}</span>}
    </div>
  );
}
